import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip
} from "chart.js"
import ChartDataLabels from "chartjs-plugin-datalabels"
import { Select } from "components/forms"
import { Bar, Doughnut, Line, Pie } from "react-chartjs-2"
import { convertNumber } from "utils"

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
)

export type MultipleChartData = {
  labels: string[]
  datasets: {
    label?: string
    data: (number | null)[]
    backgroundColor?: string | string[]
    borderColor?: string | string[]
    borderWidth?: number
    tension?: number
    fill?: boolean | "start" | "end"
    spanGaps?: boolean
  }[]
}

type ChartType = "line" | "bar" | "pie" | "doughnut"

type MultipleChartProps<T extends ChartType> = {
  className?: string
  chartType: T
  data: MultipleChartData
  options?: ChartOptions<T>
}

type MultipleChartDropdownProps = {
  value: ChartType
  onChange: (value: Exclude<ChartType, "pie" | "doughnut">) => void
}

export const MultipleChart = <T extends ChartType>({
  className,
  chartType,
  data,
  options
}: MultipleChartProps<T>) => {

  const defaultData = defaultDataset(data);

  return (
    <div className={className || "chart-container"}>
      {chartType === "line" &&  <Line data={data} options={(options as ChartOptions<"line">) ?? defaultOptionsLine(data)}/>}
      {chartType === "bar" &&  <Bar data={defaultData} options={(options as ChartOptions<"bar">) ?? defaultOptionsBar(data)}/>}
      {chartType === "pie" &&  <Pie data={defaultData} options={(options as ChartOptions<"pie">) ?? defaultOptionsPie(data)}/>}
      {chartType === "doughnut" &&  <Doughnut data={defaultData} options={(options as ChartOptions<"doughnut">) ?? defaultOptionsDoughnut(data)}/>}
    </div>
  )
}

MultipleChart.Dropdown = ({ value, onChange }: MultipleChartDropdownProps) => {
  const options: { value: ChartType; label: string }[] = [
    { value: "line", label: "Line" },
    { value: "bar", label: "Bar" }
  ]

  return (
    <div className="w-[200px] ">
      <Select
        value={options.find((item) => item.value === value)}
        options={options}
        onChange={(item) => item && onChange(item.value as Exclude<ChartType, "pie" | "doughnut">)}
      />
    </div>
  )
}

export const defaultOptionsLine = (data: MultipleChartData): ChartOptions<"line"> => {
  const nums = data.datasets[0].data.filter((v): v is number => v !== null)
  const minValue = Math.min(...nums);
  const maxValue = Math.max(...nums);

  let suggestedMin = minValue;
  let suggestedMax = maxValue;

  if (minValue < 0 && maxValue <= 0) {
    suggestedMax = 0;
  } else if (minValue >= 0 && maxValue > 0) {
    suggestedMin = 0;
  } else {
    const absMax = Math.max(Math.abs(minValue), Math.abs(maxValue));
    suggestedMin = -absMax;
    suggestedMax = absMax;
  }

  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        suggestedMin,
        suggestedMax,
        stacked: true,
        grid: { display: false },
        border: { color: 'rgb(224, 224, 224)', width: 1 },
        ticks: {
          callback: function (tickValue) {
            const numericValue = Number(tickValue);
            return numericValue.toLocaleString();
          },
        }
      },
      x: {
        grid: { display: false },
        border: { display: true },
        position: minValue < 0 && maxValue > 0 ? "center" : minValue < 0 ? "top" : "bottom",
      }
    },
    plugins: {
      filler: { propagate: false },
      title: { display: false },
      legend: { display: false },
      datalabels: { display: false }
    },
    interaction: { intersect: false }
  }
}

export const defaultOptionsBar = (data: MultipleChartData): ChartOptions<"bar"> => {
  const nums = data.datasets[0].data.filter((v): v is number => v !== null)
  const minValue = Math.min(...nums);
  const maxValue = Math.max(...nums);

  let suggestedMin = minValue;
  let suggestedMax = maxValue;

  if (minValue < 0 && maxValue <= 0) {
    suggestedMax = 0;
  } else if (minValue >= 0 && maxValue > 0) {
    suggestedMin = 0;
  } else {
    const absMax = Math.max(Math.abs(minValue), Math.abs(maxValue));
    suggestedMin = -absMax;
    suggestedMax = absMax;
  }

  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: false, color: "#E0E0E0" },
        position: minValue < 0 && maxValue > 0 ? "center" : minValue < 0 ? "top" : "bottom",
        border: { color: 'rgb(224, 224, 224)', width: 1 }
      },
      y: {
        beginAtZero: false,
        suggestedMin,
        suggestedMax,
        grid: {
          color: (context) => (context.tick.value === 0 ? 'rgb(255, 193, 7)' : 'rgba(255,255,255,0)'),
          lineWidth: (context) => (context.tick.value === 0 ? 1 : 1),
        },
        border: { color: 'rgb(224, 224, 224)', width: 1 },
        ticks: {
          callback: function (tickValue) {
            const numericValue = Number(tickValue);
            return numericValue.toLocaleString();
          },
        }
      },
    },
    plugins: {
      filler: { propagate: false },
      title: { display: false },
      legend: { display: false },
      datalabels: { display: false }
    }
  }
}

const defaultOptionsPie = (data: MultipleChartData): ChartOptions<"pie"> => {
  return {
    plugins: {
      legend: { position: "right" },
      datalabels: {
        color: '#000',
        font: { size: 10 },
        formatter: (value) => convertNumber(value).intoCurrency
      }
    }
  }
}

const defaultOptionsDoughnut = (data: MultipleChartData): ChartOptions<"doughnut"> => {
  return {
    plugins: {
      legend: { position: "right" },
      datalabels: {
        color: '#000',
        font: { size: 10 },
        formatter: (value) => value
      }
    }
  }
}

const defaultDataset = (data: MultipleChartData) => {

  const colors = [
    { backgroundColor: "rgba(0, 123, 255, 0.2)", borderColor: "rgb(0, 123, 255)" },
    { backgroundColor: "rgba(102, 16, 242, 0.2)", borderColor: "rgb(102, 16, 242)" },
    { backgroundColor: "rgba(40, 167, 69, 0.2)", borderColor: "rgb(40, 167, 69)" },
    { backgroundColor: "rgba(255, 193, 7, 0.2)", borderColor: "rgb(255, 193, 7)" },
  ];

  // Fungsi untuk mendapatkan warna acak jika lebih dari warna yang tersedia
  const getRandomColor = () => {
    const r = Math.floor(Math.random() * 256);
    const g = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);
    return {
      backgroundColor: `rgba(${r}, ${g}, ${b}, 0.2)`,
      borderColor: `rgb(${r}, ${g}, ${b})`
    };
  };

  const labels = data.labels || [];

  const colorMap: Record<string, { backgroundColor: string; borderColor: string }> = {};
  labels.forEach((label, index) => {
    colorMap[label] = colors[index] || getRandomColor(); // Gunakan warna tetap atau acak
  });

  const defaultDataset = {
    ...data,
    datasets: data.datasets.map((dataset) => ({
      ...dataset,
      backgroundColor: labels.map(label => colorMap[label]?.backgroundColor || getRandomColor().backgroundColor),
      borderColor: labels.map(label => colorMap[label]?.borderColor || getRandomColor().borderColor),
      borderWidth: 2,
    })),
  };

  return defaultDataset
}



